import { useEffect } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useRecoilState } from 'recoil';
import { MINUTE } from 'time-constants';
import { object, string } from 'yup';

import { apiConfig } from '@constants';
import { anfState } from '@store';

import { QA } from '../../helpers/Platform';
import { replaceTemplateValues } from '../../helpers/StringUtils';
import { transformObjectWithLinearFeedsToANF } from '../../helpers/transformObjectWithLinearFeedsToANF';
import { useGetUserLocation } from '../useGetUserLocation';

// Takes single argument for the current market
const anfAlertsArgumentSchema = object({
  market: string(),
});

// Only validate what we need
const anfAlertsSchema = object({
  liveFeedId: string().nullable(),
  url: string(),
  title: string().nullable(),
  network: string().nullable(),
  location: string().nullable(),
});

const getANFPollInterval = () => {
  const interval = apiConfig.ANF_POLL_INTERVAL;

  try {
    return parseInt(interval, 10) * MINUTE;
  } catch (error) {
    return 5 * MINUTE;
  }
};

const getApiUrl = ({ market }) =>
  replaceTemplateValues(apiConfig.ANF_URL, {
    id: market,
  });

const validateANF = data => data.filter(anf => anfAlertsSchema.isValidSync(anf));

const getANFAlerts = async parameters => {
  const validateArguments = await anfAlertsArgumentSchema.validate(parameters);
  const url = getApiUrl(validateArguments);
  const { data } = await axios.get(url, {
    headers: {
      accept: 'text/plain',
      'X-API-KEY': apiConfig.ANF_API_KEY,
    },
  });

  if (QA) {
    return Array.isArray(transformObjectWithLinearFeedsToANF(data))
      ? validateANF(transformObjectWithLinearFeedsToANF(data))
      : [];
  }
  return Array.isArray(data) ? validateANF(data) : [];
};

export const useGetANFAlerts = () => {
  const location = useGetUserLocation();
  const { market } = location || {};
  const [state, setState] = useRecoilState(anfState);
  const refetchInterval = getANFPollInterval();

  const { data, error, isFetched, isLoading } = useQuery(['getANFAlerts', market], () => getANFAlerts({ market }), {
    enabled: Boolean(market),
    refetchOnWindowFocus: false,
    refetchInterval,
  });

  useEffect(() => {
    const updatedAt = Date.now();

    setState({
      ...state,
      ...{
        data,
        error,
        isFetched,
        isLoading,
        updatedAt,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading, isFetched]);

  return state;
};
